/* eslint-disable no-param-reassign */
import { select as d3Select } from 'd3-selection';
import { range as d3Range } from 'd3-array';

export const NUMBER_OF_CANVASES_TO_CREATE_IN_INNERMOST_ZOOM_LEVEL = 2;
export const MAX_CANVAS_WIDTH_IN_PIXELS = 16_000;

// #region multiple canvases logic
export const getCanvasWidthInPixelsByCanvasIndex = (
  canvasIndex,
  zoomLevelWidthInPixels,
  maxCanvasWidthInPixels,
) => {
  const potentialWidthOfCanvas =
    zoomLevelWidthInPixels - canvasIndex * maxCanvasWidthInPixels;
  return Math.min(potentialWidthOfCanvas, maxCanvasWidthInPixels);
};

export const getInnermostZoomLevelBasedOnNumberOfCanvasesGenerated = (
  widthInPixels,
) => {
  const calculatedInnermostZoomLevel =
    (NUMBER_OF_CANVASES_TO_CREATE_IN_INNERMOST_ZOOM_LEVEL *
      MAX_CANVAS_WIDTH_IN_PIXELS) /
    widthInPixels;
  return calculatedInnermostZoomLevel;
};

export const getCanvasXCoordinateByCanvasIndex = (
  canvasIndex,
  maxCanvasWidthInPixels,
) => {
  return canvasIndex * maxCanvasWidthInPixels;
};

export const getZoomLevelWidthForInnermostZoomLevel = () => {
  /** The width of the chart should be equal to the width of the composite state graph
   *  since the billboard js chart width corresponds to the whole component width, this means the width of the
   * Y-axis labels, paddings and the chart. Therefore additional 46 pixels are added to compensate y axis labels and paddings
   * so we can end up with a actual chart width equal to the composite state graph and avoid misalignments.
   */
  return (
    NUMBER_OF_CANVASES_TO_CREATE_IN_INNERMOST_ZOOM_LEVEL *
      MAX_CANVAS_WIDTH_IN_PIXELS +
    46
  );
};

export const createCanvasWithIndex = (
  canvasIndex,
  zoomLevelWidthInPixels,
  visibleAreaHeightInPixels,
  maxCanvasWidthInPixels,
  referenceElement,
  canvasCssClassname,
  topValue,
) => {
  const canvasWidth = getCanvasWidthInPixelsByCanvasIndex(
    canvasIndex,
    zoomLevelWidthInPixels,
    maxCanvasWidthInPixels,
  );
  const canvasHeight = visibleAreaHeightInPixels;
  const canvasXCoordinate = getCanvasXCoordinateByCanvasIndex(
    canvasIndex,
    maxCanvasWidthInPixels,
  );
  const expectedXCordinate =
    canvasXCoordinate === 0 ? 0 : canvasXCoordinate - 1;
  const canvas = d3Select(referenceElement) // layerContainerRef.current
    .append('canvas')
    .attr('class', canvasCssClassname)
    .attr('width', canvasWidth)
    .attr('height', canvasHeight)
    .style('position', 'absolute')
    .style('left', `${expectedXCordinate}px`)
    .style('top', `${topValue}px`);

  return canvas;
};

export const getCanvasContextIndicesByElementXAndElementWidth = (
  elementX,
  elementWidthInPx,
  maxCanvasWidthInPixels,
) => {
  const startXCanvasIndex = getCanvasContextIndexByElementX(
    elementX,
    maxCanvasWidthInPixels,
  );
  const endXCanvasIndex = getCanvasContextIndexByElementX(
    elementX + elementWidthInPx,
    maxCanvasWidthInPixels,
  );
  let canvasContextIndices = [];
  if (startXCanvasIndex !== endXCanvasIndex) {
    canvasContextIndices = d3Range(startXCanvasIndex, endXCanvasIndex + 1);
  } else {
    canvasContextIndices.push(startXCanvasIndex);
  }
  return canvasContextIndices;
};

export const getCanvasContextsByElementX = (
  elementX,
  elementWidthInPx,
  maxCanvasWidthInPixels,
  canvasContexts,
) => {
  const canvasContextIndices = getCanvasContextIndicesByElementXAndElementWidth(
    elementX,
    elementWidthInPx,
    maxCanvasWidthInPixels,
  );
  return canvasContextIndices.map((currentCanvasContextIndex) => {
    return canvasContexts[currentCanvasContextIndex];
  });
};

export const getCanvasContextIndexByElementX = (
  elementX,
  maxCanvasWidthInPixels,
) => {
  const canvasIndex = Math.floor(elementX / maxCanvasWidthInPixels);
  return canvasIndex;
};

export const getLocalCanvasXByElementX = (elementX, maxCanvasWidthInPixels) => {
  // elementX is returned by the scaleFunction which was initialized/bound to the entire zoom level width range
  return elementX % maxCanvasWidthInPixels;
};

export const drawElementOnCanvas = (
  currentObjectToBeRendered,
  xPositionInPx,
  yPositionInPx,
  widthInPx,
  heightInPx,
  fillStyle,
  strokeStyle,
  canvasContexts,
  maxCanvasWidthInPixels,
  scaleFunction,
  isDynamic,
  stateArrow,
  originalFillFunction,
) => {
  // eslint-disable-next-line no-param-reassign
  canvasContexts = getCanvasContextsByElementX(
    xPositionInPx,
    widthInPx,
    maxCanvasWidthInPixels,
    canvasContexts,
  );
  drawElementOnCanvasContexts(
    currentObjectToBeRendered,
    xPositionInPx,
    yPositionInPx,
    widthInPx,
    heightInPx,
    fillStyle,
    strokeStyle,
    maxCanvasWidthInPixels,
    scaleFunction,
    canvasContexts,
    isDynamic,
    stateArrow,
    originalFillFunction,
  );
};

export const drawElementOnCanvasContexts = (
  currentObjectToBeRendered,
  xPositionInPx,
  yPositionInPx,
  widthInPx,
  heightInPx,
  fillStyle,
  strokeStyle,
  maxCanvasWidthInPixels,
  scaleFunction,
  canvasContexts,
  isDynamic,
  stateArrow,
  originalFillFunction,
) => {
  if (canvasContexts.length === 1) {
    // eslint-disable-next-line no-param-reassign
    xPositionInPx = getLocalCanvasXByElementX(
      xPositionInPx,
      maxCanvasWidthInPixels,
    );
    drawOnSpecificCanvas(
      canvasContexts[0],
      xPositionInPx,
      yPositionInPx,
      widthInPx,
      heightInPx,
      fillStyle,
      stateArrow,
      strokeStyle,
      undefined,
      originalFillFunction,
    );
  } else if (canvasContexts.length > 1) {
    canvasContexts.forEach(
      (currentCanvasContext, indexInCanvasContextsArray) => {
        let elementLocalCanvasX;
        let elementWidthInPxForCurrentCanvas;
        if (indexInCanvasContextsArray === 0) {
          elementLocalCanvasX = getLocalCanvasXByElementX(
            xPositionInPx,
            maxCanvasWidthInPixels,
          );
          elementWidthInPxForCurrentCanvas =
            maxCanvasWidthInPixels - elementLocalCanvasX;
        } else if (indexInCanvasContextsArray === canvasContexts.length - 1) {
          elementLocalCanvasX = 0;
          elementWidthInPxForCurrentCanvas = getLocalCanvasXByElementX(
            xPositionInPx + widthInPx,
            maxCanvasWidthInPixels,
          );
        } else {
          elementLocalCanvasX = 0;
          elementWidthInPxForCurrentCanvas = maxCanvasWidthInPixels;
        }
        drawOnSpecificCanvas(
          currentCanvasContext,
          elementLocalCanvasX,
          yPositionInPx,
          elementWidthInPxForCurrentCanvas,
          heightInPx,
          fillStyle,
          stateArrow,
          strokeStyle,
          indexInCanvasContextsArray,
          originalFillFunction,
        );
      },
    );
  }
};

export const drawRectangleWithDiagonals = (
  ctx,
  xPositionInPx,
  yPositionInPx,
  widthInPx,
  heightInPx,
  originalFillFunction,
  splittedActivityIndex,
) => {
  const step = 10;
  const innerStrokeWidth = 2; // Width of the inner "stroke" effect

  // First, fill the entire rectangle with white (this creates the "stroke" effect)
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(xPositionInPx, yPositionInPx, widthInPx, heightInPx);

  // Compute per-side insets so that shared edge between split parts is NOT inset
  const insetTop = innerStrokeWidth;
  const insetBottom = innerStrokeWidth;
  const insetLeft = splittedActivityIndex === 1 ? 0 : innerStrokeWidth;
  const insetRight = splittedActivityIndex === 0 ? 0 : innerStrokeWidth;

  const innerX = xPositionInPx + insetLeft;
  const innerY = yPositionInPx + insetTop;
  const innerW = widthInPx - insetLeft - insetRight;
  const innerH = heightInPx - insetTop - insetBottom;

  // Then fill the inner rectangle with the main color
  ctx.fillRect(innerX, innerY, innerW, innerH);

  // Set diagonal line color
  ctx.strokeStyle = originalFillFunction;
  ctx.lineWidth = 4;
  // Draw diagonal lines from top-right to bottom-left, clipped to inner rectangle
  ctx.save(); // Save current canvas state
  ctx.beginPath();
  ctx.rect(innerX, innerY, innerW, innerH);
  ctx.clip(); // Clip drawing to inner rectangle

  for (let i = 0; i < widthInPx + heightInPx; i += step) {
    ctx.beginPath();
    ctx.moveTo(xPositionInPx + i, yPositionInPx);
    ctx.lineTo(xPositionInPx + i - heightInPx, yPositionInPx + heightInPx);
    ctx.stroke();
  }

  xPositionInPx += innerStrokeWidth;
  yPositionInPx += innerStrokeWidth;
  widthInPx -= innerStrokeWidth * 2;
  heightInPx -= innerStrokeWidth * 2;
  ctx.restore(); // Restore canvas state
};

export const drawOnSpecificCanvas = (
  canvasContext,
  xPositionInPx,
  yPositionInPx,
  widthInPx,
  heightInPx,
  fillStyle,
  stateArrow,
  strokeStyle,
  splittedActivityIndex,
  originalFillFunction,
) => {
  let hasExplicitStrokeStyle = false;
  initializeCanvasContextWithFillAndStroke(
    canvasContext,
    fillStyle,
    strokeStyle,
  );
  if (stateArrow?.showArrow && canvasContext) {
    canvasContext.beginPath();
    canvasContext.moveTo(xPositionInPx, yPositionInPx);
    canvasContext.lineTo(xPositionInPx, yPositionInPx + heightInPx);
    canvasContext.lineTo(
      xPositionInPx + widthInPx,
      yPositionInPx + heightInPx / 2,
    );
    canvasContext.closePath();
    canvasContext.fill();
    if (stateArrow.hasStroke) {
      canvasContext.strokeStyle = 'rgba(0, 0, 0, 0.5)';
      canvasContext.lineWidth = 2;
      canvasContext.stroke();
    }
  } else if (canvasContext && canvasContext.fillRect) {
    if (fillStyle) {
      // Check if this is an empty feed grab (black rectangle) that needs special diagonal line rendering:
      // - Large black rectangles (>10px) always get diagonal lines
      // - Small black rectangles (≤10px) only get diagonal lines if they are part of a split activity across canvases
      // - Small black rectangles that are NOT split remain solid black
      if (
        (fillStyle === '#000000' && widthInPx > 10) ||
        (fillStyle === '#000000' &&
          widthInPx <= 10 &&
          splittedActivityIndex !== undefined)
      ) {
        // Draw diagonal lines for empty feed grabs (black rectangles)
        drawRectangleWithDiagonals(
          canvasContext,
          xPositionInPx,
          yPositionInPx,
          widthInPx,
          heightInPx,
          originalFillFunction,
          splittedActivityIndex,
        );
        hasExplicitStrokeStyle = true;
        canvasContext.strokeStyle = '#000';
        canvasContext.lineWidth = 1.5;
      } else {
        canvasContext.fillRect(
          xPositionInPx,
          yPositionInPx,
          widthInPx,
          heightInPx,
        );
      }
    }

    if ((strokeStyle && strokeStyle !== 'white') || hasExplicitStrokeStyle) {
      const innerStrokeWidth = 2; // Width of the inner "stroke" effect
      const insetTop = innerStrokeWidth;
      const insetBottom = innerStrokeWidth;
      const insetLeft = splittedActivityIndex === 1 ? 0 : innerStrokeWidth;
      const insetRight = splittedActivityIndex === 0 ? 0 : innerStrokeWidth;

      if (splittedActivityIndex === undefined) {
        if (hasExplicitStrokeStyle) {
          // For non-split activities with diagonal fill, draw individual lines to apply bottom border adjustment
          canvasContext.beginPath();

          // Top border
          canvasContext.moveTo(
            xPositionInPx + insetLeft / 2,
            yPositionInPx + insetTop,
          );
          canvasContext.lineTo(
            xPositionInPx + widthInPx - insetRight / 2,
            yPositionInPx + insetTop,
          );

          // Bottom border - moved up by half insetBottom to avoid touching activity below
          canvasContext.moveTo(
            xPositionInPx + insetLeft,
            yPositionInPx + heightInPx - insetBottom / 2,
          );
          canvasContext.lineTo(
            xPositionInPx + widthInPx - insetRight,
            yPositionInPx + heightInPx - insetBottom / 2,
          );

          // Left border
          canvasContext.moveTo(
            xPositionInPx + insetLeft,
            yPositionInPx + insetTop,
          );
          canvasContext.lineTo(
            xPositionInPx + insetLeft,
            yPositionInPx + heightInPx - insetBottom / 2,
          );

          // Right border
          canvasContext.moveTo(
            xPositionInPx + widthInPx - insetRight,
            yPositionInPx + insetTop,
          );
          canvasContext.lineTo(
            xPositionInPx + widthInPx - insetRight,
            yPositionInPx + heightInPx - insetBottom / 2,
          );

          canvasContext.stroke();
        } else {
          canvasContext.strokeRect(
            xPositionInPx,
            yPositionInPx,
            widthInPx,
            heightInPx,
          );
        }
      } else if (hasExplicitStrokeStyle) {
        if (splittedActivityIndex === undefined) {
          // Only draw stroke for non-split activities with diagonal fill
          canvasContext.strokeRect(
            xPositionInPx + insetLeft,
            yPositionInPx + insetTop,
            widthInPx - insetLeft - insetRight,
            heightInPx - insetTop - insetBottom,
          );
        } else {
          // For split activities, draw individual lines to avoid gaps
          // Only draw borders that are on the outer edges
          canvasContext.beginPath();

          // Top border (always drawn)
          canvasContext.moveTo(
            xPositionInPx + insetLeft / 2,
            yPositionInPx + insetTop,
          );
          canvasContext.lineTo(
            xPositionInPx + widthInPx - insetRight / 2,
            yPositionInPx + insetTop,
          );

          // Bottom border (always drawn) - moved up by half insetBottom to avoid touching activity below
          canvasContext.moveTo(
            xPositionInPx + insetLeft,
            yPositionInPx + heightInPx - insetBottom / 2,
          );
          canvasContext.lineTo(
            xPositionInPx + widthInPx - insetRight,
            yPositionInPx + heightInPx - insetBottom / 2,
          );

          // Left border (only for left part - splittedActivityIndex === 0)
          if (splittedActivityIndex === 0) {
            canvasContext.moveTo(
              xPositionInPx + insetLeft,
              yPositionInPx + insetTop,
            );
            canvasContext.lineTo(
              xPositionInPx + insetLeft,
              yPositionInPx + heightInPx - insetBottom / 2,
            );
          }

          // Right border (only for right part - splittedActivityIndex === 1)
          if (splittedActivityIndex === 1) {
            canvasContext.moveTo(
              xPositionInPx + widthInPx - insetRight,
              yPositionInPx + insetTop,
            );
            canvasContext.lineTo(
              xPositionInPx + widthInPx - insetRight,
              yPositionInPx + heightInPx - insetBottom / 2,
            );
          }

          canvasContext.stroke();
        }
      } else {
        // default outer stroke for split parts without special fill
        canvasContext.beginPath();
        canvasContext.moveTo(xPositionInPx, yPositionInPx);
        canvasContext.lineTo(xPositionInPx + widthInPx, yPositionInPx);
        canvasContext.lineTo(
          xPositionInPx + widthInPx,
          yPositionInPx + heightInPx,
        );
        canvasContext.lineTo(xPositionInPx, yPositionInPx + heightInPx);
        canvasContext.stroke();
      }
    }
    if (
      strokeStyle === 'white' &&
      !hasExplicitStrokeStyle &&
      canvasContext &&
      splittedActivityIndex === undefined
    ) {
      canvasContext.lineWidth = 1.5;
      canvasContext.strokeStyle = 'rgba(255, 255, 255, 1)';
      canvasContext.beginPath();
      canvasContext.moveTo(xPositionInPx, yPositionInPx);
      canvasContext.lineTo(xPositionInPx, yPositionInPx + heightInPx);
      canvasContext.stroke();

      canvasContext.beginPath();
      canvasContext.moveTo(xPositionInPx + widthInPx, yPositionInPx);
      canvasContext.lineTo(
        xPositionInPx + widthInPx,
        yPositionInPx + heightInPx,
      );
      canvasContext.stroke();
    }
  }
};

export const initializeCanvasContextWithFillAndStroke = (
  canvasContext,
  fillStyle,
  strokeStyle,
) => {
  if (canvasContext && canvasContext.fillRect) {
    if (fillStyle) {
      canvasContext.fillStyle = fillStyle;
    }
    if (strokeStyle && strokeStyle !== 'white') {
      canvasContext.strokeStyle = strokeStyle;
      canvasContext.lineWidth = 2;
    }
  }
};

export const initializeCanvasesForInnermostZoomLevel = (
  zoomLevelWidthInPixels,
  visibleAreaHeightInPixels,
  maxCanvasWidthInPixels,
  referenceElement,
  canvasCssClassname,
  numberOfCanvasesThatNeedToBeCreated,
  topValue,
) => {
  const canvasContexts = [];
  for (
    let canvasIndex = 0;
    canvasIndex < numberOfCanvasesThatNeedToBeCreated;
    canvasIndex++
  ) {
    canvasContexts.push(
      createCanvasWithIndex(
        canvasIndex,
        zoomLevelWidthInPixels,
        visibleAreaHeightInPixels,
        maxCanvasWidthInPixels,
        referenceElement,
        canvasCssClassname,
        topValue,
      ),
    );
  }
  return canvasContexts;
};

export const initializeCanvases = (
  zoomLevelWidthInPixels,
  visibleAreaHeightInPixels,
  maxCanvasWidthInPixels,
  referenceElement,
  canvasCssClassname,
  isDynamic,
  numberOfCanvasesThatNeedToBeCreated,
  topValue,
) => {
  // eslint-disable-next-line no-param-reassign
  numberOfCanvasesThatNeedToBeCreated = isDynamic
    ? numberOfCanvasesThatNeedToBeCreated
    : Math.ceil(zoomLevelWidthInPixels / maxCanvasWidthInPixels);
  const canvasContexts = [];
  if (isDynamic) {
    return initializeCanvasesForInnermostZoomLevel(
      zoomLevelWidthInPixels,
      visibleAreaHeightInPixels,
      maxCanvasWidthInPixels,
      referenceElement,
      canvasCssClassname,
      numberOfCanvasesThatNeedToBeCreated,
      topValue,
    );
  }
  for (
    let canvasIndex = 0;
    canvasIndex < numberOfCanvasesThatNeedToBeCreated;
    canvasIndex++
  ) {
    canvasContexts.push(
      createCanvasWithIndex(
        canvasIndex,
        zoomLevelWidthInPixels,
        visibleAreaHeightInPixels,
        maxCanvasWidthInPixels,
        referenceElement,
        canvasCssClassname,
        topValue,
      ),
    );
  }
  return canvasContexts;
};
